import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react'
import { Org, User } from '@db8/shared'
import { useUser } from '@clerk/clerk-react'
import { useAxios } from './AxiosProvider'

interface AuthContextProps {
  currentUser: User | null
  currentOrg: Org | null
  /**
   * True when Clerk auth is loaded and we've either fetched the app user/org successfully,
   * or determined the viewer is signed out.
   */
  appUserLoaded: boolean
  getCurrentUser: () => void
  setCurrentOrg: (org: Org | null) => void
  setActiveOrg: (orgId: string) => void
  showOrgModal: boolean
  setShowOrgModal: Dispatch<SetStateAction<boolean>>
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [currentOrg, setCurrentOrg] = useState<Org | null>(null)
  const [showOrgModal, setShowOrgModal] = useState(false)
  const { isLoaded, isSignedIn, user } = useUser()
  const axios = useAxios()
  const [appUserLoaded, setAppUserLoaded] = useState(false)

  const fetchInFlightRef = useRef(false)
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef<number | null>(null)

  const getCurrentUser = async () => {
    if (!user?.id) return
    try {
      const res = await axios.get(`/users/${user.id}`)
      if (res.data) {
        setCurrentUser(res.data)
        if (res.data.activeOrg) setCurrentOrg(res.data.activeOrg)
      }
    } catch (error) {
      // no-op
      // Don't set user/org state if the request failed
    }
  }

  const setActiveOrg = async (orgId?: string) => {
    if (!currentUser) return
    
    try {
      let activeOrgId = orgId ? orgId : currentUser.activeOrg?.id
      if (!activeOrgId) return
      
      const res = await axios.put(`/users/${currentUser.id}`, {
        activeOrgId,
      })
      if (res.data?.activeOrg) {
        setCurrentOrg(res.data.activeOrg)
      }
    } catch (error) {
      // no-op
    }
  }

  useEffect(() => {
    // Cleanup any pending retry when auth state changes/unmounts
    return () => {
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    // Wait for Clerk to finish hydrating before we do anything. This avoids firing
    // `/users/:id` before tokens are ready (which can 401 and leave UI stuck).
    if (!isLoaded) {
      setAppUserLoaded(false)
      return
    }

    // Signed out: clear app user state and mark loaded so logged-out UI can render.
    if (!isSignedIn || !user?.id) {
      fetchInFlightRef.current = false
      retryCountRef.current = 0
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      setCurrentUser(null)
      setCurrentOrg(null)
      setAppUserLoaded(true)
      return
    }

    // Already have the right user loaded
    if (currentUser?.id === user.id && appUserLoaded) return

    if (fetchInFlightRef.current) return

    let cancelled = false

    const fetchUser = async () => {
      if (cancelled) return
      if (!user?.id) return

      fetchInFlightRef.current = true
      setAppUserLoaded(false)

      try {
        const res = await axios.get(`/users/${user.id}`)
        if (cancelled) return

        if (res.data) {
          setCurrentUser(res.data)
          setCurrentOrg(res.data.activeOrg ?? null)
        } else {
          setCurrentUser(null)
          setCurrentOrg(null)
        }

        retryCountRef.current = 0
        setAppUserLoaded(true)
      } catch (_error) {
        // Transient auth/token timing: retry a few times with backoff.
        retryCountRef.current += 1
        const attempt = retryCountRef.current

        if (attempt <= 3) {
          const delayMs = [250, 750, 1500][attempt - 1] ?? 1500
          retryTimerRef.current = window.setTimeout(() => {
            fetchInFlightRef.current = false
            fetchUser()
          }, delayMs)
          return
        }

        // Give up: allow UI to render rather than staying stuck in "loading" forever.
        setAppUserLoaded(true)
      } finally {
        fetchInFlightRef.current = false
      }
    }

    void fetchUser()

    return () => {
      cancelled = true
    }
  }, [isLoaded, isSignedIn, user?.id, axios, currentUser?.id, appUserLoaded])

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        currentOrg,
        appUserLoaded,
        setCurrentOrg,
        setActiveOrg,
        getCurrentUser,
        showOrgModal,
        setShowOrgModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
